import BasicModal from '__components/Modals/presets/BasicModal';
import { Dispatch, FC, SetStateAction, useState } from 'react';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { toastVariant, useToast } from '__components/ToastProvider';
import { useMutation } from '@tanstack/react-query';
import TeamMemberService from 'services/TeamMemberService';
import { AxiosError } from 'axios';
import {
  CreateIntegrationSecretPayload,
  IntegrationSecret,
  IntegrationSecretErrors,
  MenuAction,
  UpdateIntegrationSecretPayload,
} from './types';
import { getErrorMessage } from '__hooks/useErrors';
import { getOriginalError } from '__services/AxiosService';
import { useFlashTransition } from '__hooks/useFlashState';
import TextInput from '__components/Inputs/TextInput';
import DateTimeInput from '__components/Inputs/DateTimeInput';
import ConfirmDeletionModal from '__components/Modals/presets/ConfirmDeletion';
import { KizenTypography } from '__app/typography';

export type SelectedIntegrationSecret = {
  id: string;
  data: IntegrationSecret | null;
  action: MenuAction | null;
};

type CreateModalProps = {
  setSelectedIntegrationSecret: Dispatch<
    SetStateAction<SelectedIntegrationSecret>
  >;
  refetchIntegrationSecrets: () => void;
};

type EditModalProps = {
  selectedIntegrationSecret: SelectedIntegrationSecret;
  setSelectedIntegrationSecret: Dispatch<
    SetStateAction<SelectedIntegrationSecret>
  >;
  refetchIntegrationSecrets: () => void;
};

type ViewModalProps = {
  selectedIntegrationSecret: SelectedIntegrationSecret;
  setSelectedIntegrationSecret: Dispatch<
    SetStateAction<SelectedIntegrationSecret>
  >;
};

type DeleteModalProps = {
  selectedIntegrationSecret: SelectedIntegrationSecret;
  setSelectedIntegrationSecret: Dispatch<
    SetStateAction<SelectedIntegrationSecret>
  >;
  refetchIntegrationSecrets: () => void;
};

export const CreateIntegrationSecretModal: FC<CreateModalProps> = ({
  setSelectedIntegrationSecret,
  refetchIntegrationSecrets,
}) => {
  const { t } = useTranslation();
  const [showToast] = useToast();
  const [{ description, value }, setFormData] =
    useState<CreateIntegrationSecretPayload>({
      description: '',
      value: '',
    });
  const [errors, setErrors] = useState<IntegrationSecretErrors>({});

  const handleHide = useCallback(() => {
    setFormData({ description: '', value: '' });
    setErrors({});
    setSelectedIntegrationSecret({ id: '', data: null, action: null });
  }, [setSelectedIntegrationSecret]);

  const [message, showMessage, flashErrorMessage, cancelFlash] =
    useFlashTransition();

  const { mutate, status } = useMutation({
    mutationFn: async (payload: CreateIntegrationSecretPayload) => {
      await TeamMemberService.createIntegrationSecret(payload);
    },
    onSuccess: () => {
      refetchIntegrationSecrets();
      handleHide();
    },
    onError: (error: AxiosError<IntegrationSecretErrors>) => {
      const axiosError = getOriginalError(error);
      if (axiosError.value || axiosError.description) {
        setErrors({
          value: axiosError.value,
          description: axiosError.description,
        });
        flashErrorMessage(
          axiosError.description
            ? { description: axiosError.description }
            : { value: axiosError.value }
        );
      } else {
        const message =
          getErrorMessage(axiosError) ??
          t(
            'Integration Secret could not be created, please refresh your browser and try again.'
          );
        showToast({
          message,
          variant: toastVariant.FAILURE,
        });
      }
    },
  });

  const handleSubmit = useCallback(() => {
    mutate({ description, value });
  }, [description, mutate, value]);

  const handleChangeValue = useCallback(
    (value: string) => {
      cancelFlash();
      setErrors((prev) => ({ ...prev, value: undefined }));
      setFormData((prev) => ({ ...prev, value }));
    },
    [cancelFlash]
  );

  const handleChangeDescription = useCallback(
    (description: string) => {
      cancelFlash();
      setErrors((prev) => ({ ...prev, description: undefined }));
      setFormData((prev) => ({ ...prev, description }));
    },
    [cancelFlash]
  );

  return (
    <BasicModal
      heading={t('Create Integration Secret')}
      onConfirm={handleSubmit}
      onHide={handleHide}
      show
      size="small"
      disabled={status === 'pending'}
    >
      <TextInput
        value={description}
        onChange={handleChangeDescription}
        label={t('Descrption')}
        placeholder={t('Enter Description')}
        margin
        error={errors.description}
        validate={
          message?.description && { message: message.description, showMessage }
        }
        inModal
      />
      <TextInput
        value={value}
        onChange={handleChangeValue}
        label={t('Integration Secret')}
        placeholder={t('Enter Integration Secret')}
        error={errors.value}
        validate={message?.value && { message: message.value, showMessage }}
        inModal
      />
    </BasicModal>
  );
};

export const EditIntegrationSecretModal: FC<EditModalProps> = ({
  selectedIntegrationSecret,
  setSelectedIntegrationSecret,
  refetchIntegrationSecrets,
}) => {
  const { t } = useTranslation();
  const [showToast] = useToast();
  const [{ description, value }, setFormData] =
    useState<UpdateIntegrationSecretPayload>({
      description: selectedIntegrationSecret.data?.description || '',
      value: '',
    });
  const [errors, setErrors] = useState<IntegrationSecretErrors>({});

  const handleHide = useCallback(() => {
    setFormData({ description: '', value: '' });
    setErrors({});
    setSelectedIntegrationSecret({ id: '', data: null, action: null });
  }, [setSelectedIntegrationSecret]);

  const [message, showMessage, flashErrorMessage, cancelFlash] =
    useFlashTransition();

  const { mutate, status } = useMutation({
    mutationFn: async (payload: UpdateIntegrationSecretPayload) => {
      await TeamMemberService.updateIntegrationSecret(
        selectedIntegrationSecret.id,
        payload
      );
    },
    onSuccess: () => {
      refetchIntegrationSecrets();
      handleHide();
    },
    onError: (error: AxiosError<IntegrationSecretErrors>) => {
      const axiosError = getOriginalError(error);
      if (axiosError.value || axiosError.description) {
        setErrors({
          value: axiosError.value,
          description: axiosError.description,
        });
        flashErrorMessage(
          axiosError.description
            ? { description: axiosError.description }
            : { value: axiosError.value }
        );
      } else {
        const message =
          getErrorMessage(axiosError) ??
          t(
            'Integration Secret could not be updated, please refresh your browser and try again.'
          );
        showToast({
          message,
          variant: toastVariant.FAILURE,
        });
      }
    },
  });

  const handleSubmit = useCallback(() => {
    mutate({ description, value: value || undefined });
  }, [description, mutate, value]);

  const handleChangeValue = useCallback(
    (value: string) => {
      cancelFlash();
      setErrors((prev) => ({ ...prev, value: undefined }));
      setFormData((prev) => ({ ...prev, value }));
    },
    [cancelFlash]
  );

  const handleChangeDescription = useCallback(
    (description: string) => {
      cancelFlash();
      setErrors((prev) => ({ ...prev, description: undefined }));
      setFormData((prev) => ({ ...prev, description }));
    },
    [cancelFlash]
  );

  return (
    <BasicModal
      heading={t('Update Integration Secret')}
      onConfirm={handleSubmit}
      onHide={handleHide}
      show
      size="small"
      disabled={status === 'pending'}
    >
      <TextInput
        value={selectedIntegrationSecret.data?.api_name}
        label={t('Api Name')}
        margin
        disabled
      />
      <TextInput
        value={description}
        onChange={handleChangeDescription}
        label={t('Descrption')}
        placeholder={t('Enter Description')}
        margin
        error={errors.description}
        validate={
          message?.description && { message: message.description, showMessage }
        }
        inModal
      />
      <TextInput
        value={value}
        onChange={handleChangeValue}
        label={t('Integration Secret')}
        placeholder={
          selectedIntegrationSecret.data?.obfuscated_value ||
          t('Enter Integration Secret')
        }
        error={errors.value}
        validate={message?.value && { message: message.value, showMessage }}
        inModal
      />
    </BasicModal>
  );
};

export const ViewIntegrationSecretModal: FC<ViewModalProps> = ({
  selectedIntegrationSecret,
  setSelectedIntegrationSecret,
}) => {
  const { t } = useTranslation();

  const handleHide = useCallback(() => {
    setSelectedIntegrationSecret({ id: '', data: null, action: null });
  }, [setSelectedIntegrationSecret]);

  const {
    description,
    obfuscated_value,
    api_name,
    created,
    updated,
    created_by,
    updated_by,
    business_plugin_app,
  } = selectedIntegrationSecret.data!;

  return (
    <BasicModal
      heading={description}
      onConfirm={handleHide}
      buttonText={t('Close')}
      actionBtnColor={'blue'}
      leftBtn={null}
      onHide={handleHide}
      show
      size="small"
    >
      <TextInput value={api_name} label={t('Api Name')} margin disabled />
      <TextInput value={description} label={t('Descrption')} margin disabled />
      <TextInput
        value={obfuscated_value}
        label={t('Integration Secret')}
        disabled
        margin
      />
      <DateTimeInput
        value={updated}
        label={t('Last Updated Date')}
        disabled
        margin
      />
      <TextInput
        value={updated_by?.display_name}
        label={t('Last Updated By')}
        disabled
        margin
      />
      <DateTimeInput
        value={created}
        label={t('Created Date')}
        disabled
        margin
      />
      <TextInput
        value={created_by?.display_name}
        label={t('Created By')}
        disabled
        margin
      />
      <TextInput
        value={business_plugin_app?.plugin_app_name}
        label={t('Plugin App')}
        disabled
      />
    </BasicModal>
  );
};

export const DeleteIntegrationSecretModal: FC<DeleteModalProps> = ({
  selectedIntegrationSecret,
  setSelectedIntegrationSecret,
  refetchIntegrationSecrets,
}) => {
  const { t } = useTranslation();
  const [showToast] = useToast();

  const handleHide = useCallback(() => {
    setSelectedIntegrationSecret({ id: '', data: null, action: null });
  }, [setSelectedIntegrationSecret]);

  const { mutate, status } = useMutation({
    mutationFn: async () => {
      await TeamMemberService.deleteIntegrationSecret(
        selectedIntegrationSecret.id
      );
    },
    onSuccess: () => {
      refetchIntegrationSecrets();
      handleHide();
    },
    onError: (error: AxiosError) => {
      const message =
        getErrorMessage(getOriginalError(error)) ||
        t(
          'Integration Secret could not be deleted, please refresh your browser and try again.'
        );

      showToast({
        message,
        variant: toastVariant.FAILURE,
      });
    },
  });

  const handleSubmit = useCallback(() => {
    mutate();
  }, [mutate]);

  return (
    <ConfirmDeletionModal
      heading={t('Please Confirm Deletion')}
      onConfirm={handleSubmit}
      onHide={handleHide}
      show
      disabled={status === 'pending'}
    >
      <KizenTypography>
        {t(
          'Deleting this integration secret will make all subsequent requests using it denied by Kizen.'
        )}
      </KizenTypography>
    </ConfirmDeletionModal>
  );
};
